import type { Entity, Model } from "../../design/types";

/**
 * Normalize reference fields before storing: single ref = plain id,
 * array ref = id[]. Accepts either populated Entity objects (`{ id, ... }`)
 * or bare string ids and coerces to the storage shape.
 *
 * Shared between Local and Firebase Database implementations because the
 * transformation is storage-format-agnostic — both serialize references
 * as the same scalar-or-array-of-ids shape.
 */
export function depopulate(model: Model, data: Record<string, unknown>): Record<string, unknown> {
    if (!Array.isArray(model.properties)) return data;
    const result: Record<string, unknown> = { ...data };
    const toId = (v: unknown): string | null =>
        v == null ? null
        : typeof v === 'object' && !Array.isArray(v) && 'id' in v ? (v as Entity).id
        : typeof v === 'string' ? v
        : null;
    for (const prop of model.properties) {
        if (typeof prop !== 'object' || prop.type !== 'reference') continue;
        if (!(prop.name in data)) continue;
        const value = data[prop.name];
        if (prop.array) {
            const arr = value == null ? [] : Array.isArray(value) ? value : [value];
            result[prop.name] = arr.map(toId).filter((v): v is string => v != null);
        } else {
            const scalar = Array.isArray(value) ? value[0] : value;
            result[prop.name] = toId(scalar);
        }
    }
    return result;
}
